import Clear from "@mui/icons-material/Clear";
import { debounce } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRouter } from "next/router";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import RecentSearches from "recent-searches";
import { bindActionCreators } from "redux";
import Color from "../../../../assets/colors";
import { checkLightCastTokenTimeOut } from "../../../../helpers/auth";
import { convertIdsToUrl } from "../../../../helpers/filter_management";
import useTriggerSnackbar from "../../../../hooks/useTriggerSnackbar";
import {
  clearSearchSuggestions,
  fetchSearchSuggestions,
  getStateRegions,
  getTrendingJobKeywords,
  updateJobListFilter,
  updateJobSearchKeyword,
} from "../../../../redux/actions/job_action";
import {
  fetchingJobList,
  getUser,
  updateJobTitleSuggestionsDropdownStatus,
} from "../../../../redux/actions/user_action";
import { store } from "../../../../redux/stores/store";
import JobSearchOptionsMenu from "../../../jobs/JobSearchOptions/JobSearchOptionsMenu/JobSearchOptionsMenu";
import {
  BlankContainer,
  BriefcaseIconStyled,
  DropdownItem,
  DropdownListContainer,
  DropdownListWrapper,
  DropdownText,
  IconButtonStyled,
  InputBaseStyled,
  KeywordInputWrapper,
  LocationCount,
  LocationInputStyled,
  LocationInputWrapper,
  MapPinIconStyled,
  SearchBarDropdownWrapper,
  SearchBarGrid,
  SearchBarStyled,
  SearchDivider,
  SearchFieldsContainer,
  SearchIconStyledContained,
  SearchIconStyledOriginal,
  SearchSuggestionContainer,
  SuggestionsContainer,
  TitleGrid,
} from "./styles";

function HomepageSearchBar(props) {
  // Logics for search bar
  const { t } = useTranslation();

  const jobTitles = useSelector((state) => state.user?.onboardingJobTitles);
  const jobTitleDropdownStatus = useSelector(
    (state) => state.user.jobTitleDropdownStatus
  );
  const jobListFilter = useSelector((state) => state.jobs?.jobListFilter);
  const stateList = useSelector((state) => state.jobs?.stateRegions);
  const specialisationList = useSelector((state) => state.jobs?.specialisation);

  const textInputRef = useRef(null);
  const searchBarRef = useRef(null);
  const keywordInputWrapperRef = useRef(null);
  const locationButtonRef = useRef(null);
  const locationMenuRef = useRef(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const setSearchBoth = (val) => {
    searchRef.current = val;
    setSearch(val);
  };
  const [searchBarFocus, setSearchBarFocus] = useState(false);
  const [searchingJobs, setSearchingJobs] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [width, setWidth] = useState(0);
  const [location, setLocation] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationFocus, setLocationFocus] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [pendingSearchKeyword, setPendingSearchKeyword] = useState(null);
  const [latestSelectedFilter, setLatestSelectedFilter] = useState(
    jobListFilter || {}
  );
  const [triggerSnackbarFunc] = useTriggerSnackbar();

  const fetchUser = getUser();
  const isLoggedIn = Object.keys(fetchUser ?? {}).length > 0;

  const router = useRouter();

  const dispatch = useDispatch();

  const fetchJob = useMemo(
    () =>
      debounce((value) => {
        dispatch(fetchingJobList(value));
      }, 300),
    [dispatch]
  );

  const debouncedFetchJob = useCallback((value) => fetchJob(value), []);

  const getLocationLabel = (selected = []) => {
    if (!selected?.length) return "";
    if (selected.length === 1) return selected[0]; // Show the single location
    return t("homepage.location_label"); // Show number of locations
  };

  const selectedLocations = latestSelectedFilter?.state || [];
  const locationCount = selectedLocations.length;

  useEffect(() => {
    checkLightCastTokenTimeOut();
  }, []);

  useEffect(() => {
    // if (!locationOpen) {
    setLocation(getLocationLabel(selectedLocations));
    setLocationSearch("");
    // }
  }, [locationOpen, selectedLocations]);

  const screenMobile = useMediaQuery("(min-width:768px)");
  const isMobile = useMediaQuery("(max-width:640px)");

  const searches = new RecentSearches({
    namespace: "recent-searches",
    ranking: "TIME",
    limit: 5,
  });

  const compareSize = () => {
    setWidth(window.innerWidth);
  };

  useEffect(() => {
    setLatestSelectedFilter(jobListFilter || {});
  }, [jobListFilter]);

  const onSearchBarClicked = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const searchOnClickListener = (e) => {
      if (event !== e) {
        setSearchBarFocus(false);
        window.removeEventListener("click", searchOnClickListener);
      }
    };

    if (!searchBarFocus) {
      setSearchBarFocus(true);
      window.addEventListener("click", searchOnClickListener);
    }

    if (width <= 768) {
      store.getState().jobs.fromMobileNavbar = false;
    }
  };

  const onSearchUpdate = (event) => {
    const value = event?.target?.value ?? "";

    searchRef.current = value;
    setSearch(value);

    dispatch(updateJobSearchKeyword({ keyword: value }));

    if (value) {
      debouncedFetchJob(value);
    }

    if (value.length < 2) {
      dispatch(updateJobTitleSuggestionsDropdownStatus(false));
      props.clearSearchSuggestions?.();
      return;
    }

    dispatch(updateJobTitleSuggestionsDropdownStatus(true));

    setTimeout(() => {
      props.fetchSearchSuggestions({ keyword: value });
    }, 100);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      setSearchingJobs(true);
      handleSearchButtonClicked();
    }
  };

  const handleClearSearchInput = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setSearchBoth("");
    dispatch(updateJobSearchKeyword({ keyword: "" }));
    textInputRef.current.value = "";
  };

  const handleOpenLocationMenu = () => {
    setLocationOpen(true);
    setLocationFocus(true);
  };

  const handleFocusLocation = () => {
    if (!locationOpen) {
      handleOpenLocationMenu();
      return;
    }
    setLocationFocus(true);
  };

  const handleChangeLocation = (value) => {
    setLocation(value);
    setLocationSearch(value);
    handleOpenLocationMenu();
  };

  const handleClearLocation = (event) => {
    event?.stopPropagation?.();

    const clearedFilter = {
      ...(latestSelectedFilter || {}),
      state: [],
    };

    setLocation("");
    setLocationSearch("");
    setLatestSelectedFilter(clearedFilter);
    dispatch(updateJobListFilter(clearedFilter));
    if (locationOpen) setLocationFocus(true);
  };

  const handleSelectionChange = useCallback((selectedFilter) => {
    setLatestSelectedFilter(selectedFilter || {});
  }, []);

  const slugifyKeyword = (text = "") =>
    text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const runSearch = useCallback(
    (keywordValue) => {
      const keyword = keywordValue?.trim() || "";
      const filterToApply = { ...latestSelectedFilter, keyword };

      dispatch(updateJobListFilter(filterToApply));
      dispatch(updateJobSearchKeyword({ keyword }));

      const { path, query } = convertIdsToUrl(
        { ...filterToApply, state: [] },
        specialisationList || [],
        stateList || []
      );

      const keywordPath = keyword
        ? `/${slugifyKeyword(keyword)}-jobs`
        : "/jobs";
      const pathname = path || keywordPath;

      const selectedStates = (filterToApply?.state || []).filter(Boolean);
      const stateQuery = selectedStates
        .map(
          (value) =>
            `state=${encodeURIComponent(
              value.toLowerCase().trim().replace(/\s+/g, "-")
            )}`
        )
        .join("&");

      const combinedQuery = [query, stateQuery].filter(Boolean).join("&");
      const url = combinedQuery ? `${pathname}?${combinedQuery}` : pathname;
      router.push(url);

      setSearchBarFocus(false);
    },
    [dispatch, latestSelectedFilter, router, specialisationList, stateList]
  );

  const handleLocationApply = (selectedFilter) => {
    const appliedFilter = selectedFilter || {};
    setLatestSelectedFilter(appliedFilter);
    dispatch(updateJobListFilter(appliedFilter));

    // Update the input field immediately
    setLocation(getLocationLabel(appliedFilter?.state));
    setLocationSearch("");

    setLocationFocus(false);
    setLocationOpen(false);
  };

  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     if (
  //       locationButtonRef.current?.contains(event.target) ||
  //       locationMenuRef.current?.contains(event.target)
  //     ) {
  //       return;
  //     }
  //     setLocationFocus(false); // triggers the above effect
  //   }

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, []);

  // Reset loading state when navigation completes
  useEffect(() => {
    const handleRouteChange = () => {
      setSearchingJobs(false);
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeError", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeError", handleRouteChange);
    };
  }, [router.events]);

  const handleSearchButtonClicked = (keywordOverride = null) => {
    const keywordRaw =
      typeof keywordOverride === "string" ? keywordOverride : searchRef.current;
    const keywordValue = (keywordRaw || "").trim();

    // Use the latest selection from location menu (not just latestSelectedFilter)
    const selectedStates = latestSelectedFilter?.state || [];

    // Block guest search if nothing entered
    // if (
    //   !isLoggedIn &&
    //   keywordValue.length === 0 &&
    //   selectedStates.length === 0
    // ) {
    //   triggerSnackbarFunc({
    //     snackbarMessage: "Enter a job title or location to start a search",
    //     severity: "error",
    //   });
    //   return;
    // }

    // Close dropdown
    setLocationOpen(false);
    setLocationFocus(false);

    // Trigger search immediately with **currently selected locations**
    setSearchingJobs(true);
    runSearch(keywordValue, latestSelectedFilter);
  };

  const handleSearchSuggestionClicked = (suggestion) => (event) => {
    event.preventDefault();
    event.stopPropagation();

    setSearch(suggestion);
    dispatch(updateJobSearchKeyword({ keyword: suggestion }));
    dispatch(updateJobTitleSuggestionsDropdownStatus(false));
  };

  const SearchSuggestions = (props) => {
    const { suggestions, search, handleSearchSuggestionClicked } = props;

    const boldMatchingText = (suggestion, searchInput) => {
      const n = suggestion.toLowerCase();
      const q = searchInput.toLowerCase();
      const x = n.indexOf(q);

      // If suggestion has no matching characters, return suggestion
      if (!q || x === -1) {
        return (
          <SearchSuggestionContainer
            onClick={handleSearchSuggestionClicked(suggestion)}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: suggestion,
              }}
            />
          </SearchSuggestionContainer>
        );
      }
      // Else bold characters that match search input
      const l = q.length;
      const boldedSearch =
        suggestion.substr(0, x) +
        "<b>" +
        suggestion.substr(x, l) +
        "</b>" +
        suggestion.substr(x + l);

      return (
        <SearchSuggestionContainer
          onClick={handleSearchSuggestionClicked(suggestion)}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: boldedSearch,
            }}
          />
        </SearchSuggestionContainer>
      );
    };

    return (
      <Fragment>
        {suggestions.length > 0 && (
          <BlankContainer container justifyContent="center">
            <SuggestionsContainer>
              {suggestions.map((suggestion, index) => {
                return (
                  <div key={index}>
                    {boldMatchingText(suggestion.name, search)}
                  </div>
                );
              })}
            </SuggestionsContainer>
          </BlankContainer>
        )}
      </Fragment>
    );
  };

  useEffect(() => {
    let params = { limit: 10 };
    props.getTrendingJobKeywords(params);
    dispatch(getStateRegions());

    setRecentSearches(searches.getRecentSearches("recent-searches"));

    // Compare once and add resize listener on "componentDidMount"
    window.addEventListener("resize", compareSize);

    if (!width) {
      setWidth(window.innerWidth);
    }

    return () => {
      // Remove resize listener again on "componentWillUnmount"
      window.removeEventListener("resize", compareSize);
      setRecentSearches(searches.getRecentSearches("recent-searches"));
    };
  }, []);

  // For the header animation
  const shuffleText = ["ultimate", "desired", "fun", "cool", "liked"];
  const [pointer, setPointer] = useState(0);

  // For the pulsing animation
  const [time, setTime] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      setTime(time + 1);

      if (pointer === 4) {
        setPointer(0);
      } else {
        setPointer(pointer + 1);
      }
    }, 1300);
    return () => {
      clearTimeout(timer);
    };
  }, [time, pointer]);

  // For the search bar search icon hover effect
  const [hover, setHover] = useState("false");

  const onSelectJobTitle = (suggestedJobTitle) => {
    setSearchBoth(suggestedJobTitle);
    dispatch(updateJobSearchKeyword({ keyword: suggestedJobTitle }));
    dispatch(updateJobTitleSuggestionsDropdownStatus(false));
  };

  let blurTimeout;

  const handleBlur = () => {
    if (blurTimeout) {
      clearTimeout(blurTimeout);
    }

    blurTimeout = setTimeout(() => {
      dispatch(updateJobTitleSuggestionsDropdownStatus(false));
      setSearchBarFocus(false);
    }, 500); // 500ms delay
  };

  return (
    <TitleGrid container justifyContent="center" alignItems="center">
      <SearchBarGrid
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <SearchBarStyled ref={searchBarRef}>
          <SearchFieldsContainer>
            <KeywordInputWrapper ref={keywordInputWrapperRef}>
              <BriefcaseIconStyled />
              <InputBaseStyled
                onMouseEnter={() => {
                  setHover("true");
                }}
                onMouseLeave={() => {
                  setHover("false");
                }}
                placeholder={t("homepage.search_placeholder")}
                inputProps={{
                  style: { fontStyle: search.length > 0 ? "normal" : "italic" },
                }}
                onClick={onSearchBarClicked}
                onChange={onSearchUpdate}
                onFocus={() => {
                  setLocationOpen(false);
                  setLocationFocus(false);
                  dispatch(updateJobTitleSuggestionsDropdownStatus(true));
                  setSearchBarFocus(true);
                }}
                onBlur={handleBlur}
                onKeyDown={onKeyDown}
                value={search}
                inputRef={textInputRef}
                data-testid="homepage-search-input"
              />
              {searchBarFocus && search.length > 2 && (
                <IconButton
                  onClick={handleClearSearchInput}
                  sx={{ borderRadius: "5px" }}
                >
                  <Clear sx={{ fontSize: "1.5rem" }} />
                </IconButton>
              )}
            </KeywordInputWrapper>

            <SearchDivider />

            <LocationInputWrapper
              ref={locationButtonRef}
              $active={locationFocus || locationOpen}
              onMouseEnter={() => {
                setHover("true");
              }}
              onMouseLeave={() => {
                setHover("false");
              }}
              onClick={handleOpenLocationMenu}
            >
              <MapPinIconStyled />
              <LocationInputStyled
                value={location}
                onFocus={handleFocusLocation}
                onChange={(e) => handleChangeLocation(e.target.value)}
                placeholder={t("homepage.location_placeholder")}
                inputProps={{
                  style: {
                    fontStyle: location.length > 0 ? "normal" : "italic",
                  },
                }}
              />
              {locationCount > 1 ? (
                <LocationCount>{locationCount}</LocationCount>
              ) : null}
              {locationOpen && (location?.length > 0 || locationCount > 0) && (
                <IconButton
                  onClick={handleClearLocation}
                  sx={{ borderRadius: "5px" }}
                >
                  <Clear sx={{ fontSize: "1.5rem" }} />
                </IconButton>
              )}
            </LocationInputWrapper>
          </SearchFieldsContainer>
          <IconButtonStyled
            onMouseDown={(e) => e.preventDefault()} // keep focus
            onClick={(e) => {
              e.stopPropagation(); // <-- prevent document click listener
              handleSearchButtonClicked();
            }}
            // onMouseDown={(e) => {
            //   e.preventDefault(); // 🔥 prevents focus hijacking
            // }}
            // onClick={(e) => {
            //   e.preventDefault();
            //   e.stopPropagation();
            //   if (!searchingJobs) {
            //     handleSearchButtonClicked();
            //   }
            // }}
            hover={hover}
            searchbarfocus={searchBarFocus || locationFocus}
            disabled={searchingJobs}
            data-testid="homepage-search-btn"
          >
            {searchingJobs ? (
              <CircularProgress
                size={screenMobile ? 24 : 18}
                sx={{
                  color:
                    searchBarFocus || locationFocus
                      ? Color.white
                      : Color.hiredlyPurple,
                }}
              />
            ) : searchBarFocus || locationFocus ? (
              <SearchIconStyledContained
                hover={hover}
                focused="true"
                sx={{ fontSize: screenMobile ? "34px" : "24px" }}
              />
            ) : (
              <SearchIconStyledOriginal
                hover={hover}
                focused="false"
                sx={{ fontSize: screenMobile ? "34px" : "24px" }}
              />
            )}
          </IconButtonStyled>

          {/* {searchBarFocus || locationFocus ? (
            <IconButtonStyled
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!searchingJobs) {
                  handleSearchButtonClicked();
                }
              }}
              hover={hover}
              searchbarfocus={searchBarFocus || locationFocus}
              disabled={searchingJobs}
            >
              {searchingJobs ? (
                <CircularProgress
                  size={screenMobile ? 24 : 18}
                  sx={{ color: Color.white }}
                />
              ) : (
                <SearchIconStyledContained
                  hover={hover}
                  focused={(searchBarFocus || locationFocus).toString()}
                  sx={{ fontSize: screenMobile ? "34px" : "24px" }}
                />
              )}
            </IconButtonStyled>
          ) : (
            <Fragment>
              <IconButtonStyled
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!searchingJobs) {
                    handleSearchButtonClicked();
                  }
                }}
                hover={hover}
                disabled={searchingJobs}
              >
                {searchingJobs ? (
                  <CircularProgress
                    size={screenMobile ? 24 : 18}
                    sx={{ color: Color.hiredlyPurple }}
                  />
                ) : (
                  <SearchIconStyledOriginal
                    hover={hover}
                    focused={(searchBarFocus || locationFocus).toString()}
                    sx={{ fontSize: screenMobile ? "34px" : "24px" }}
                  />
                )}
              </IconButtonStyled>
            </Fragment>
          )} */}
          {isMobile && jobTitleDropdownStatus && search?.length > 2 && (
            <SearchBarDropdownWrapper>
              <DropdownListContainer
                searchLength={search?.length}
                style={{ position: "relative" }}
              >
                {jobTitles?.slice(0, 5).map((item, index) => {
                  return (
                    <DropdownItem
                      key={`${item}-${index}`}
                      onClick={() => onSelectJobTitle(item)}
                    >
                      <DropdownText className="dropdown">{item}</DropdownText>
                    </DropdownItem>
                  );
                })}
              </DropdownListContainer>
            </SearchBarDropdownWrapper>
          )}
        </SearchBarStyled>
        <JobSearchOptionsMenu
          filterOpen={locationOpen}
          setFilterOpen={(open) => {
            setLocationOpen(open);
            if (!open) {
              setLocationFocus(false);
            }
          }}
          anchorRef={isMobile ? searchBarRef : locationButtonRef}
          mode="location"
          onApply={handleLocationApply}
          locationSearch={locationSearch}
          menuRef={locationMenuRef}
          onSelectionChange={handleSelectionChange}
          stretchToParent={!isMobile}
          locationBorderColor={Color.chipGrey}
          locationBoxShadow="3px 3px 0px 0px rgba(0,0,0,1)"
          locationBorderRadius="10px"
        />
        {/* {search?.length > 2 ? (
          <Fade in={search.length > 2} timeout={10000}>
            <SuggestionsContainerWrapper>
              <SearchSuggestions
                suggestions={jobTitles}
                search={search}
                handleSearchSuggestionClicked={handleSearchSuggestionClicked}
              />
            </SuggestionsContainerWrapper>
          </Fade>
        ) : null} */}
        {!isMobile && jobTitleDropdownStatus && search?.length > 2 && (
          <DropdownListWrapper>
            <DropdownListContainer searchLength={search?.length}>
              {
                search?.length > 2
                  ? jobTitles?.slice(0, 5).map((item, index) => {
                      return (
                        <DropdownItem
                          key={`${item}-${index}`}
                          onClick={() => onSelectJobTitle(item)}
                        >
                          <DropdownText className="dropdown">
                            {item}
                          </DropdownText>
                        </DropdownItem>
                      );
                    })
                  : null
                // <HomepageTrending />
              }
            </DropdownListContainer>
          </DropdownListWrapper>
        )}
      </SearchBarGrid>
    </TitleGrid>
  );
}

const mapStateToProps = (state) => {
  return {
    trendingJobKeywords: state.jobs.trendingJobKeywords,
    //searchData: state.jobs.searchData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getTrendingJobKeywords: bindActionCreators(
      getTrendingJobKeywords,
      dispatch
    ),
    fetchSearchSuggestions: bindActionCreators(
      fetchSearchSuggestions,
      dispatch
    ),
    clearSearchSuggestions: bindActionCreators(
      clearSearchSuggestions,
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HomepageSearchBar);
